// PGA flag schema — data-driven definitions for the 21 supported flag types.
// UI renders generically from this config; adding a type is a data change.

export type PgaFieldType =
  | 'text'
  | 'select'
  | 'date'
  | 'yesno'
  | 'quantity'
  | 'number';

export interface PgaField {
  key: string;
  storageKey?: string;
  label: string;
  inputType: PgaFieldType;
  required?: boolean;
  options?: string[];
  uomOptions?: string[];
  placeholder?: string;
  workbookStorageKeys?: string[];
  // Workbook keys a definition maps onto a specific repeater instance. `path`
  // addresses a slot inside a composite value (e.g. ["amount"] of a quantity)
  // and is relative to the field, with a leading field key tolerated.
  workbookRepeaterSources?: PgaWorkbookRepeaterSource[];
}

export interface PgaWorkbookRepeaterSource {
  storageKey: string;
  index: number;
  path?: string[];
}

export interface PgaSubGroup {
  key: string;
  label: string;
  fields: PgaField[];
  repeatable?: boolean;
  maxInstances?: number;
  itemLabel?: string;
  defaultOpen?: boolean;
}

export type PgaCode =
  | 'FDA'
  | 'NHT'
  | 'CPS'
  | 'DTC'
  | 'DEA'
  | 'AMS'
  | 'APH_CORE'
  | 'APH_LACEY'
  | 'ATF'
  | 'EPA_TSCA'
  | 'EPA_HFC'
  | 'EPA_PST'
  | 'EPA_VNE'
  | 'FSIS'
  | 'FWS'
  | 'NMF_370'
  | 'NMF_AMR'
  | 'NMF_COA'
  | 'NMF_SIM'
  | 'OMC'
  | 'TTB';

export interface PgaFlagSchema {
  code: PgaCode;
  agency: string;
  displayName: string;
  subGroups: PgaSubGroup[];
}

export interface PgaWorkbookDefinitionField {
  header: string;
  section: string;
  storageKey: string;
  inputType: 'text' | 'date' | 'yesno';
  uiTarget?:
    | { kind: 'value'; key: string }
    | {
        kind: 'repeater';
        group: string;
        index: number;
        key: string;
        path?: string[];
      };
}

export interface PgaWorkbookDefinition {
  code: PgaCode;
  sourceCode: string;
  fields: PgaWorkbookDefinitionField[];
}

const hasPgaValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

// An explicitly stored "" is a user clearing the field, which must survive
// normalization; a key that was never written is genuinely absent.
const hasPgaKey = (values: Record<string, unknown>, key: string): boolean =>
  key in values && values[key] !== undefined && values[key] !== null;

const normalizeDeclareValue = (value: unknown): unknown => {
  if (!hasPgaValue(value)) return value;
  const normalized = String(value).trim().toLowerCase();
  if (['y', 'yes', 'true', '1', 'required'].includes(normalized)) {
    return 'Required';
  }
  if (['n', 'no', 'false', '0', 'not required'].includes(normalized)) {
    return 'Not Required';
  }
  return value;
};

// Fills a repeater instance slot without overwriting anything already there.
// `path` targets a slot inside a composite value (quantities), so the value
// object is cloned rather than replaced.
const setPgaRepeaterValueIfMissing = (
  instance: Record<string, unknown>,
  fieldKey: string,
  path: string[] | undefined,
  value: unknown,
) => {
  const relative = path?.length
    ? path[0] === fieldKey
      ? path.slice(1)
      : path
    : [];
  if (relative.length === 0) {
    if (!hasPgaKey(instance, fieldKey)) instance[fieldKey] = value;
    return;
  }
  const clone = (candidate: unknown): Record<string, unknown> =>
    candidate && typeof candidate === 'object'
      ? { ...(candidate as Record<string, unknown>) }
      : {};
  const root = clone(instance[fieldKey]);
  let cursor = root;
  for (let i = 0; i < relative.length - 1; i += 1) {
    const next = clone(cursor[relative[i]]);
    cursor[relative[i]] = next;
    cursor = next;
  }
  const leaf = relative[relative.length - 1];
  if (!hasPgaKey(cursor, leaf)) cursor[leaf] = value;
  instance[fieldKey] = root;
};

const setPgaValueIfMissing = (
  values: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  if (!hasPgaValue(values[key]) && hasPgaValue(value)) values[key] = value;
};

// Master-workbook values remain stored under their canonical `workbook.*`
// keys for fidelity. This adapter projects them into the existing editor
// schema so uploaded and manually entered flags render and count identically.
export function normalizePgaFlagPayload(
  code: PgaCode,
  sourceValues: Record<string, unknown>,
  sourceRepeaters: Record<string, Record<string, unknown>[]>,
  schema: PgaFlagSchema = PGA_FLAG_SCHEMAS[code],
): {
  values: Record<string, unknown>;
  repeaters: Record<string, Record<string, unknown>[]>;
} {
  const values = { ...sourceValues };
  const repeaters = Object.fromEntries(
    Object.entries(sourceRepeaters).map(([key, items]) => [
      key,
      items.map((item) => ({ ...item })),
    ]),
  );
  // Project workbook-sourced scalars onto the schema field they map to, so a
  // `workbook.*` storage key stays visible (and counted) in the editor. An
  // empty canonical value is an intentional clear, not a missing one — only an
  // absent key falls back to the workbook value.
  for (const group of schema.subGroups) {
    if (group.repeatable) continue;
    for (const field of group.fields) {
      if (!field.workbookStorageKeys?.length) continue;
      const targetKey = field.storageKey ?? `${group.key}.${field.key}`;
      if (hasPgaKey(values, targetKey)) continue;
      const source = field.workbookStorageKeys.find((storageKey) =>
        hasPgaValue(values[storageKey]),
      );
      if (source) values[targetKey] = values[source];
    }
  }

  const workbookPrefix = `workbook.${code.toLowerCase()}`;
  // The canonical value wins when present — it carries edits saved after the
  // workbook import, which would otherwise be reverted on every reopen.
  const canonicalDeclare = values['declare.declare'];
  const declaration = hasPgaValue(canonicalDeclare)
    ? canonicalDeclare
    : values[`${workbookPrefix}.declare`];
  if (hasPgaValue(declaration)) {
    values['declare.declare'] = normalizeDeclareValue(declaration);
  }

  if (code === 'DTC') {
    setPgaValueIfMissing(
      values,
      'declare.reference',
      values['workbook.dtc.itar.exemption.number'],
    );
    setPgaValueIfMissing(
      values,
      'declare.category',
      values['workbook.dtc.license.type'],
    );
    setPgaValueIfMissing(
      values,
      'declare.noticeNumber',
      values['workbook.dtc.license.number'] ?? values['declare.noticeNumber'],
    );
    setPgaValueIfMissing(
      values,
      'declare.registrationNumber',
      values['workbook.dtc.registration.number'],
    );
  }

  if (code === 'DEA') {
    setPgaValueIfMissing(
      values,
      'productDetails.countryOfOrigin',
      values['workbook.dea.country.of.shipment'],
    );
    setPgaValueIfMissing(
      values,
      'declare.permitNumber',
      values['workbook.dea.registration.number'],
    );
    setPgaValueIfMissing(
      values,
      'productDetails.form',
      values['workbook.dea.form.id'],
    );

    const constituents = [...(repeaters.constituents ?? [])];
    for (let index = 1; index <= 6; index += 1) {
      const productCode =
        values[`workbook.dea.constituents.product.code.${index}`];
      const weight = values[`workbook.dea.constituents.weight.${index}`];
      const uom = values[`workbook.dea.constituents.weight.uom.${index}`];
      if (![productCode, weight, uom].some(hasPgaValue)) continue;
      const current = { ...(constituents[index - 1] ?? {}) };
      if (!hasPgaValue(current.name) && hasPgaValue(productCode)) {
        current.name = productCode;
      }
      const currentAmount =
        current.amount && typeof current.amount === 'object'
          ? { ...(current.amount as Record<string, unknown>) }
          : {};
      if (!hasPgaValue(currentAmount.amount) && hasPgaValue(weight)) {
        currentAmount.amount = weight;
      }
      if (!hasPgaValue(currentAmount.uom) && hasPgaValue(uom)) {
        currentAmount.uom = uom;
      }
      if (Object.keys(currentAmount).length > 0) current.amount = currentAmount;
      constituents[index - 1] = current;
    }
    if (constituents.some((item) => item && Object.keys(item).length > 0)) {
      repeaters.constituents = constituents;
    }
  }

  if (code === 'APH_LACEY') {
    const species =
      values['workbook.aphapl.constituent.element.species.name.1'];
    setPgaValueIfMissing(values, 'productDetails.scientificName', species);
    if (
      hasPgaValue(species) &&
      repeaters.harvesting?.[0]?.species === species
    ) {
      const first = { ...repeaters.harvesting[0] };
      delete first.species;
      const harvesting = [...repeaters.harvesting];
      if (Object.keys(first).length > 0) harvesting[0] = first;
      else harvesting.shift();
      if (harvesting.length > 0) repeaters.harvesting = harvesting;
      else delete repeaters.harvesting;
    }
  }

  for (const group of schema.subGroups) {
    if (!group.repeatable) continue;
    for (const field of group.fields) {
      // Positional keys and definition-mapped sources both address a repeater
      // instance by index; forEach skips the holes a sparse positional list
      // leaves behind.
      const sources: PgaWorkbookRepeaterSource[] = [];
      field.workbookStorageKeys?.forEach((storageKey, index) => {
        if (storageKey) sources.push({ storageKey, index });
      });
      field.workbookRepeaterSources?.forEach((source) => {
        if (source?.storageKey && source.index >= 0) sources.push(source);
      });
      if (sources.length === 0) continue;
      const items = [...(repeaters[group.key] ?? [])];
      sources.forEach(({ storageKey, index, path }) => {
        const value = values[storageKey];
        if (!hasPgaValue(value)) return;
        const current = { ...(items[index] ?? {}) };
        setPgaRepeaterValueIfMissing(current, field.key, path, value);
        items[index] = current;
      });
      if (items.some((item) => item && Object.keys(item).length > 0)) {
        repeaters[group.key] = items;
      }
    }
  }

  return { values, repeaters };
}

const YESNO: string[] = ['Yes', 'No'];
const UOM = ['EA', 'KG', 'LB', 'L', 'GAL', 'M3', 'PCS', 'DZ'];

// Reusable field builders
const declare = (label = 'Declare'): PgaField => ({
  key: 'declare',
  label,
  inputType: 'select',
  required: true,
  options: ['Required', 'Not Required', 'May Proceed'],
});
const txt = (key: string, label: string, required = false): PgaField => ({
  key,
  label,
  inputType: 'text',
  required,
});
const sel = (
  key: string,
  label: string,
  options: string[],
  required = false,
): PgaField => ({ key, label, inputType: 'select', options, required });
const date = (key: string, label: string, required = false): PgaField => ({
  key,
  label,
  inputType: 'date',
  required,
});
const yn = (key: string, label: string): PgaField => ({
  key,
  label,
  inputType: 'yesno',
});
const qty = (key: string, label: string): PgaField => ({
  key,
  label,
  inputType: 'quantity',
  uomOptions: UOM,
});
const num = (key: string, label: string): PgaField => ({
  key,
  label,
  inputType: 'number',
});

// Common repeatable entity sub-group
const entitiesGroup = (max = 6): PgaSubGroup => ({
  key: 'entities',
  label: 'Entities',
  repeatable: true,
  maxInstances: max,
  itemLabel: 'entity',
  fields: [
    sel(
      'role',
      'Role',
      [
        'Manufacturer',
        'Shipper',
        'Grower',
        'Consolidator',
        'Importer of Record',
        'Ultimate Consignee',
        'Broker',
      ],
      true,
    ),
    txt('name', 'Legal name', true),
    txt('firmsCode', 'FIRMS / Facility code'),
    txt('addressLine', 'Address line'),
    txt('city', 'City'),
    txt('stateProvince', 'State / Province'),
    txt('postalCode', 'Postal code'),
    txt('country', 'Country'),
    txt('dunsOrEin', 'DUNS / EIN / Tax ID'),
    txt('contactName', 'Contact name'),
    txt('contactEmail', 'Contact email'),
    txt('contactPhone', 'Contact phone'),
  ],
});

const productDetails = (extra: PgaField[] = []): PgaSubGroup => ({
  key: 'productDetails',
  label: 'Product Details',
  defaultOpen: true,
  fields: [
    txt('productCode', 'Product / commodity code'),
    txt('productName', 'Product name', true),
    txt('description', 'Description'),
    txt('brand', 'Brand / trade name'),
    txt('model', 'Model / SKU'),
    txt('intendedUse', 'Intended use'),
    txt('hsCode', 'HS / HTS code'),
    txt('countryOfOrigin', 'Country of origin'),
    ...extra,
  ],
});

const quantitiesGroup = (): PgaSubGroup => ({
  key: 'quantities',
  label: 'Quantities',
  fields: [
    qty('netQuantity', 'Net quantity'),
    qty('grossWeight', 'Gross weight'),
    qty('volume', 'Volume'),
    num('numberOfPackages', 'Number of packages'),
    txt('packagingType', 'Packaging type'),
  ],
});

const affirmationGroup = (labels: string[]): PgaSubGroup => ({
  key: 'affirmation',
  label: 'Affirmation of Compliance',
  repeatable: true,
  maxInstances: 8,
  itemLabel: 'affirmation',
  fields: [
    sel('code', 'AofC code', labels, true),
    txt('qualifier', 'Qualifier / registration #'),
  ],
});

const constituentsGroup = (): PgaSubGroup => ({
  key: 'constituents',
  label: 'Constituent Elements',
  repeatable: true,
  maxInstances: 12,
  itemLabel: 'constituent',
  fields: [
    txt('name', 'Constituent / ingredient name', true),
    txt('casNumber', 'CAS number'),
    qty('amount', 'Amount per unit'),
    num('percent', '% by weight'),
    yn('active', 'Active ingredient?'),
  ],
});

const harvestingGroup = (): PgaSubGroup => ({
  key: 'harvesting',
  label: 'Harvesting Details',
  repeatable: true,
  maxInstances: 6,
  itemLabel: 'harvest source',
  fields: [
    txt('species', 'Species (scientific name)', true),
    txt('harvestCountry', 'Country of harvest'),
    txt('harvestRegion', 'Region / area'),
    date('harvestDate', 'Harvest date'),
    txt('permitNumber', 'Permit / license #'),
    qty('harvestQty', 'Harvest quantity'),
  ],
});

const licenseGroup = (): PgaSubGroup => ({
  key: 'license',
  label: 'License / Permit',
  fields: [
    txt('licenseNumber', 'License number', true),
    date('issueDate', 'Issue date'),
    date('expiryDate', 'Expiry date'),
    txt('issuingAuthority', 'Issuing authority'),
  ],
});

// Build all schemas. Field volumes reflect template ranges without hardcoding
// every alternate. Small types are exhaustive; large types use full sub-group
// stacks with repeaters (each repeater instance multiplies the effective count).

export const PGA_FLAG_SCHEMAS: Record<PgaCode, PgaFlagSchema> = {
  FDA: {
    code: 'FDA',
    agency: 'FDA',
    displayName: 'Food & Drug Administration (97 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'programCode',
            'FDA program code',
            ['FOO', 'DRU', 'DEV', 'COS', 'BIO', 'RAD'],
            true,
          ),
          sel('processingCode', 'Processing code', [
            'ANA',
            'CER',
            'ENT',
            'IMP',
            'REV',
          ]),
          sel('intendedUse', 'Intended use code', ['001', '010', '081', '999']),
        ],
      },
      productDetails([
        txt('upcCode', 'UPC code'),
        txt('lotNumber', 'Lot number'),
        txt('expirationDate', 'Expiration date'),
      ]),
      quantitiesGroup(),
      affirmationGroup(['ACS', 'REG', 'FCE', 'SID', 'DUN', 'PRN', 'SLT']),
      constituentsGroup(),
      entitiesGroup(),
    ],
  },
  NHT: {
    code: 'NHT',
    agency: 'NHTSA',
    displayName: 'NHTSA — Vehicle Safety (33 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'box',
            'HS-7 box',
            [
              '1',
              '2A',
              '2B',
              '3',
              '4',
              '5',
              '6',
              '7',
              '8',
              '9',
              '10',
              '11',
              '12',
              '13',
            ],
            true,
          ),
          txt('vehicleType', 'Vehicle type'),
        ],
      },
      productDetails([
        txt('vin', 'VIN'),
        txt('makeModel', 'Make / model'),
        txt('modelYear', 'Model year'),
        txt('engineNumber', 'Engine number'),
      ]),
      quantitiesGroup(),
      entitiesGroup(),
    ],
  },
  CPS: {
    code: 'CPS',
    agency: 'CPSC',
    displayName: 'Consumer Product Safety Commission (57 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'cpscCode',
            'CPSC product code',
            ['CHI', 'TOY', 'ADU', 'FUR', 'APP'],
            true,
          ),
          yn('childrensProduct', "Children's product?"),
        ],
      },
      productDetails([
        txt('modelName', 'Model name'),
        txt('productDescription', 'Detailed description'),
      ]),
      quantitiesGroup(),
      affirmationGroup(['CPC', 'GCC', 'LAB', 'TES']),
      {
        key: 'testing',
        label: 'Testing / Certification',
        fields: [
          txt('labName', 'Third-party lab'),
          txt('labId', 'CPSC lab ID'),
          date('testDate', 'Test date'),
          txt('certNumber', 'Certificate #'),
        ],
      },
      entitiesGroup(),
    ],
  },
  DTC: {
    code: 'DTC',
    agency: 'DDTC / DOT',
    displayName: 'DOT / DDTC Notice (5 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          txt('noticeNumber', 'License / notice number', true),
          sel('category', 'License / notice type', [
            'Hazmat',
            'Munitions',
            'Other',
          ]),
          date('noticeDate', 'Notice date'),
          txt('reference', 'ITAR exemption / reference #'),
          txt('registrationNumber', 'Registration number'),
        ],
      },
    ],
  },
  DEA: {
    code: 'DEA',
    agency: 'DEA',
    displayName: 'Drug Enforcement Administration (22 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'schedule',
            'Controlled schedule',
            ['I', 'II', 'III', 'IV', 'V'],
            true,
          ),
          txt('permitNumber', 'DEA permit #', true),
        ],
      },
      productDetails([
        txt('substance', 'Controlled substance'),
        txt('form', 'Form / dosage'),
      ]),
      quantitiesGroup(),
      constituentsGroup(),
      licenseGroup(),
      entitiesGroup(4),
    ],
  },
  AMS: {
    code: 'AMS',
    agency: 'USDA AMS',
    displayName: 'USDA Agricultural Marketing Service (63 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'commodity',
            'Commodity',
            ['Cotton', 'Peanut', 'Tobacco', 'Dairy', 'Organic Produce'],
            true,
          ),
          sel('gradeStandard', 'Grade / standard', [
            'US #1',
            'US #2',
            'Choice',
            'Prime',
          ]),
        ],
      },
      productDetails([
        txt('varietal', 'Varietal / cultivar'),
        txt('grade', 'Grade'),
        txt('packDate', 'Pack date'),
      ]),
      quantitiesGroup(),
      affirmationGroup(['MID', 'USG', 'ORG', 'COP', 'PHY']),
      {
        key: 'certification',
        label: 'Certification',
        fields: [
          txt('certNumber', 'Certificate #'),
          txt('certifyingBody', 'Certifying body'),
          date('certDate', 'Issue date'),
          date('certExpiry', 'Expiry date'),
        ],
      },
      harvestingGroup(),
      entitiesGroup(),
    ],
  },
  APH_CORE: {
    code: 'APH_CORE',
    agency: 'APHIS CORE',
    displayName: 'APHIS Core (109 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'commodityGroup',
            'APHIS commodity group',
            [
              'Plant products',
              'Live animals',
              'Animal products',
              'Veterinary biologics',
            ],
            true,
          ),
          sel('regulatoryProgram', 'Regulatory program', [
            'Plant Protection',
            'Veterinary Services',
            'Wildlife Services',
          ]),
        ],
      },
      productDetails([
        txt('scientificName', 'Scientific name (genus / species)', true),
        txt('commonName', 'Common name'),
        txt('processingLevel', 'Processing level'),
        txt('preservation', 'Preservation method'),
        txt('purpose', 'Purpose'),
      ]),
      quantitiesGroup(),
      licenseGroup(),
      affirmationGroup(['PHY', 'PIN', 'SAN', 'IRR', 'HTR', 'COL', 'TIL']),
      {
        key: 'treatment',
        label: 'Treatment',
        repeatable: true,
        maxInstances: 4,
        itemLabel: 'treatment',
        fields: [
          sel('type', 'Treatment type', [
            'Heat',
            'Cold',
            'Fumigation',
            'Irradiation',
          ]),
          txt('agent', 'Agent'),
          num('temperature', 'Temperature'),
          num('duration', 'Duration (hrs)'),
          date('treatmentDate', 'Treatment date'),
          txt('facility', 'Treatment facility'),
        ],
      },
      harvestingGroup(),
      constituentsGroup(),
      entitiesGroup(),
    ],
  },
  APH_LACEY: {
    code: 'APH_LACEY',
    agency: 'APHIS Lacey',
    displayName: 'APHIS Lacey Act (36 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'productType',
            'Product type',
            ['Timber', 'Plant', 'Paper'],
            true,
          ),
        ],
      },
      productDetails([
        txt('scientificName', 'Scientific name (genus / species)', true),
      ]),
      quantitiesGroup(),
      harvestingGroup(),
      entitiesGroup(),
    ],
  },
  ATF: {
    code: 'ATF',
    agency: 'ATF',
    displayName: 'Bureau of Alcohol, Tobacco, Firearms (18 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'category',
            'Category',
            ['Firearms', 'Ammunition', 'Explosives', 'Alcohol', 'Tobacco'],
            true,
          ),
        ],
      },
      productDetails([
        txt('serialNumber', 'Serial number'),
        txt('caliber', 'Caliber / gauge'),
        txt('manufacturerCode', 'Manufacturer code'),
      ]),
      quantitiesGroup(),
      licenseGroup(),
      entitiesGroup(3),
    ],
  },
  EPA_TSCA: {
    code: 'EPA_TSCA',
    agency: 'EPA TSCA',
    displayName: 'EPA TSCA — Section 13 (2 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          sel(
            'tscaCertification',
            'TSCA Section 13 certification',
            ['Positive — complies with TSCA', 'Negative — not subject to TSCA'],
            true,
          ),
          txt('signatureName', 'Certifying signature (printed name)', true),
        ],
      },
    ],
  },
  EPA_HFC: {
    code: 'EPA_HFC',
    agency: 'EPA HFC',
    displayName: 'EPA Hydrofluorocarbons (16 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'hfcType',
            'HFC substance',
            ['HFC-134a', 'HFC-125', 'HFC-143a', 'HFC-32'],
            true,
          ),
          txt('allocationCert', 'Allocation certificate #', true),
        ],
      },
      productDetails([txt('blendName', 'Blend name'), num('gwp', 'GWP')]),
      quantitiesGroup(),
      entitiesGroup(3),
    ],
  },
  EPA_PST: {
    code: 'EPA_PST',
    agency: 'EPA Pesticide',
    displayName: 'EPA Pesticides (66 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'fifraStatus',
            'FIFRA status',
            ['Registered', 'Unregistered — experimental', 'Devices only'],
            true,
          ),
          txt('epaRegNumber', 'EPA registration number', true),
        ],
      },
      productDetails([
        txt('productName', 'Trade name'),
        txt('formulation', 'Formulation type'),
      ]),
      quantitiesGroup(),
      constituentsGroup(),
      {
        key: 'notice',
        label: 'Notice of Arrival',
        fields: [
          txt('noaNumber', 'NOA number'),
          date('noaDate', 'NOA date'),
          sel('noaStatus', 'NOA status', ['Approved', 'Refused', 'Pending']),
        ],
      },
      entitiesGroup(),
    ],
  },
  EPA_VNE: {
    code: 'EPA_VNE',
    agency: 'EPA Vehicle & Engine',
    displayName: 'EPA Vehicles & Engines (40 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel('form', 'EPA form', ['3520-1', '3520-21'], true),
          sel('code', 'Code', ['A', 'B', 'E', 'F', 'L', 'Z']),
        ],
      },
      productDetails([
        txt('engineFamily', 'Engine family'),
        txt('engineSerial', 'Engine serial #'),
        num('modelYear', 'Model year'),
        num('displacement', 'Displacement (cc)'),
      ]),
      quantitiesGroup(),
      entitiesGroup(4),
    ],
  },
  FSIS: {
    code: 'FSIS',
    agency: 'FSIS',
    displayName: 'USDA FSIS Meat / Poultry / Egg (39 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'species',
            'Species',
            ['Beef', 'Pork', 'Poultry', 'Egg', 'Lamb'],
            true,
          ),
          txt('estNumber', 'Establishment #', true),
        ],
      },
      productDetails([
        txt('productForm', 'Product form'),
        txt('slaughterDate', 'Slaughter date'),
      ]),
      quantitiesGroup(),
      affirmationGroup(['EST', 'COL', 'STA', 'SLA']),
      entitiesGroup(4),
    ],
  },
  FWS: {
    code: 'FWS',
    agency: 'US Fish & Wildlife',
    displayName: 'US Fish & Wildlife Service (50 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel('form', 'Form 3-177 code', ['EX', 'IM', 'RE', 'TS'], true),
          sel('cites', 'CITES status', ['I', 'II', 'III', 'None']),
        ],
      },
      productDetails([
        txt('scientificName', 'Scientific name', true),
        txt('commonName', 'Common name'),
        txt('sourceCode', 'Source code'),
        txt('purposeCode', 'Purpose code'),
      ]),
      quantitiesGroup(),
      licenseGroup(),
      harvestingGroup(),
      entitiesGroup(4),
    ],
  },
  NMF_370: {
    code: 'NMF_370',
    agency: 'NMFS 370',
    displayName: 'NMFS Tuna Tracking (28 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel('form', 'Form 370', ['Yes', 'No — not applicable'], true),
          sel('dolphinSafe', 'Dolphin-safe status', [
            'Dolphin-safe',
            'Non-dolphin-safe',
          ]),
        ],
      },
      productDetails([
        txt('species', 'Tuna species'),
        txt('gearType', 'Gear type'),
        txt('catchArea', 'Catch area (FAO)'),
      ]),
      quantitiesGroup(),
      entitiesGroup(3),
    ],
  },
  NMF_AMR: {
    code: 'NMF_AMR',
    agency: 'NMFS AMR',
    displayName: 'NMFS Antarctic Marine Resources (5 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          txt('dcdNumber', 'Dissostichus catch document #', true),
          txt('vesselName', 'Vessel name'),
          txt('flagState', 'Flag state'),
          date('catchDate', 'Catch date'),
        ],
      },
    ],
  },
  NMF_COA: {
    code: 'NMF_COA',
    agency: 'NMFS COA',
    displayName: 'NMFS Certificate of Admissibility (57 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'fishery',
            'Fishery',
            ['Shark', 'Swordfish', 'Bluefin tuna', 'Toothfish'],
            true,
          ),
          txt('coaNumber', 'COA number', true),
        ],
      },
      productDetails([
        txt('species', 'Species'),
        txt('processingState', 'Processing state'),
      ]),
      quantitiesGroup(),
      harvestingGroup(),
      entitiesGroup(),
    ],
  },
  NMF_SIM: {
    code: 'NMF_SIM',
    agency: 'NMFS SIMP',
    displayName: 'NMFS Seafood Import Monitoring (65 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          txt('nmfsPermit', 'NMFS IFTP / IFQ permit #', true),
          sel('chainOfCustody', 'Chain of custody complete?', YESNO),
        ],
      },
      productDetails([
        txt('species', 'Species (ASFIS)'),
        txt('productForm', 'Product form'),
        txt('catchArea', 'Catch area (FAO)'),
      ]),
      quantitiesGroup(),
      harvestingGroup(),
      {
        key: 'landing',
        label: 'Landing',
        fields: [
          date('landingDate', 'Landing date'),
          txt('landingPort', 'Landing port'),
          txt('landingCountry', 'Landing country'),
          qty('landingWeight', 'Landing weight'),
        ],
      },
      entitiesGroup(),
    ],
  },
  OMC: {
    code: 'OMC',
    agency: 'OMC',
    displayName: 'Office of Munitions Control (39 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'usml',
            'USML category',
            ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'],
            true,
          ),
          txt('dspNumber', 'DSP-5 / DSP-73 license #', true),
        ],
      },
      productDetails([
        txt('partNumber', 'Part number'),
        txt('configuration', 'Configuration'),
      ]),
      quantitiesGroup(),
      licenseGroup(),
      entitiesGroup(4),
    ],
  },
  TTB: {
    code: 'TTB',
    agency: 'TTB',
    displayName: 'Alcohol & Tobacco Tax and Trade Bureau (9 fields)',
    subGroups: [
      {
        key: 'declare',
        label: 'Declare',
        defaultOpen: true,
        fields: [
          declare(),
          sel(
            'commodity',
            'Commodity',
            ['Distilled spirits', 'Wine', 'Malt beverage', 'Tobacco'],
            true,
          ),
          txt('colaNumber', 'COLA number', true),
          txt('brand', 'Brand name'),
          num('abv', 'ABV (%)'),
          qty('volume', 'Volume'),
          num('proof', 'Proof'),
          txt('vintage', 'Vintage / year'),
        ],
      },
    ],
  },
};

const normalizePgaSection = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const workbookFieldLabel = (
  definition: PgaWorkbookDefinition,
  field: PgaWorkbookDefinitionField,
): string => {
  const prefixes = [definition.sourceCode, definition.code.replace(/_/g, ' ')]
    .map(normalizePgaSection)
    .filter(Boolean);
  let label = field.header.replace(/\s*\[\d+\]\s*$/, '').trim();
  const normalized = normalizePgaSection(label);
  for (const prefix of prefixes) {
    if (normalized.startsWith(`${prefix} `)) {
      label = label.slice(prefix.length).trim();
      break;
    }
  }
  return label || field.header;
};

const workbookFieldKey = (label: string): string =>
  `workbook_${normalizePgaSection(label).replace(/\s+/g, '_')}`;

const findClosestPgaGroup = (
  groups: PgaSubGroup[],
  section: string,
): PgaSubGroup => {
  const normalized = normalizePgaSection(section);
  const exact = groups.find(
    (group) => normalizePgaSection(group.label) === normalized,
  );
  if (exact) return exact;

  const preferredLabels = (() => {
    if (/entit|exporter|official|facility|contact/.test(normalized)) {
      return ['Entities', 'Product Details'];
    }
    if (/quantit|weight/.test(normalized)) return ['Quantities'];
    if (/ingredient|constituent/.test(normalized)) {
      return ['Constituent Elements', 'Product Details'];
    }
    if (/harvest|landing/.test(normalized)) {
      return ['Harvesting Details', 'Product Details'];
    }
    if (/license|permit/.test(normalized)) {
      return ['License / Permit', 'Declare'];
    }
    if (/program|declare/.test(normalized)) return ['Declare'];
    if (
      /product|vehicle|firearm|wildlife|line|breed|source|shipment|inspection|routing/.test(
        normalized,
      )
    ) {
      return ['Product Details', 'Declare'];
    }
    return ['Product Details', 'Declare'];
  })();
  for (const label of preferredLabels) {
    const group = groups.find((candidate) => candidate.label === label);
    if (group) return group;
  }
  return groups[0];
};

const findScalarSchemaTarget = (
  groups: PgaSubGroup[],
  key: string,
): PgaField | undefined => {
  for (const group of groups) {
    if (group.repeatable) continue;
    const field = group.fields.find(
      (candidate) =>
        (candidate.storageKey ?? `${group.key}.${candidate.key}`) === key,
    );
    if (field) return field;
  }
  return undefined;
};

export function resolvePgaFlagSchema(
  code: PgaCode,
  definition?: PgaWorkbookDefinition | null,
): PgaFlagSchema {
  const base = PGA_FLAG_SCHEMAS[code];
  if (!definition) return base;
  const groups = base.subGroups.map((group) => ({
    ...group,
    fields: group.fields.map((field) => ({ ...field })),
  }));
  const indexedFallbacks = new Map<
    string,
    {
      group: PgaSubGroup;
      label: string;
      inputType: PgaFieldType;
      storageKeys: string[];
    }
  >();

  for (const field of definition.fields) {
    const target = field.uiTarget;
    if (target?.kind === 'value') {
      const existing = findScalarSchemaTarget(groups, target.key);
      if (existing) {
        // The value stays stored under its `workbook.*` key, so keep the
        // association — normalization projects it onto this schema field.
        if (!existing.workbookStorageKeys?.includes(field.storageKey)) {
          existing.workbookStorageKeys = [
            ...(existing.workbookStorageKeys ?? []),
            field.storageKey,
          ];
        }
        continue;
      }
      const group = findClosestPgaGroup(groups, field.section);
      const key = target.key.split('.').pop() || workbookFieldKey(field.header);
      if (!group.fields.some((candidate) => candidate.key === key)) {
        group.fields.push({
          key,
          storageKey: target.key,
          label: workbookFieldLabel(definition, field),
          inputType: field.inputType,
        });
      }
      continue;
    }
    if (target?.kind === 'repeater') {
      const targetGroup = groups.find(
        (group) => group.repeatable && group.key === target.group,
      );
      if (targetGroup) {
        let existing = targetGroup.fields.find(
          (candidate) => candidate.key === target.key,
        );
        if (!existing) {
          existing = {
            key: target.key,
            label: workbookFieldLabel(definition, field),
            inputType: field.inputType,
          };
          targetGroup.fields.push(existing);
        }
        // The value stays stored under its `workbook.*` key, so keep the
        // instance index (and any path within a composite value) — otherwise
        // normalization has no way to project it onto this repeater field.
        if (
          !existing.workbookRepeaterSources?.some(
            (source) => source.storageKey === field.storageKey,
          )
        ) {
          existing.workbookRepeaterSources = [
            ...(existing.workbookRepeaterSources ?? []),
            {
              storageKey: field.storageKey,
              index: target.index,
              path: target.path,
            },
          ];
        }
        continue;
      }
    }

    const indexed = field.header.match(/\[(\d+)\]\s*$/);
    const closestGroup = findClosestPgaGroup(groups, field.section);
    if (indexed && closestGroup.repeatable) {
      const label = workbookFieldLabel(definition, field);
      const key = `${closestGroup.key}:${workbookFieldKey(label)}`;
      const entry = indexedFallbacks.get(key) ?? {
        group: closestGroup,
        label,
        inputType: field.inputType,
        storageKeys: [],
      };
      entry.storageKeys[Number(indexed[1]) - 1] = field.storageKey;
      indexedFallbacks.set(key, entry);
      continue;
    }

    const dynamicKey = workbookFieldKey(field.header);
    if (
      !closestGroup.fields.some(
        (candidate) =>
          candidate.storageKey === field.storageKey ||
          candidate.workbookStorageKeys?.includes(field.storageKey) ||
          candidate.key === dynamicKey,
      )
    ) {
      closestGroup.fields.push({
        key: dynamicKey,
        ...(closestGroup.repeatable
          ? { workbookStorageKeys: [field.storageKey] }
          : { storageKey: field.storageKey }),
        label: workbookFieldLabel(definition, field),
        inputType: field.inputType,
      });
    }
  }

  for (const {
    group,
    label,
    inputType,
    storageKeys,
  } of indexedFallbacks.values()) {
    const key = workbookFieldKey(label);
    if (group.fields.some((field) => field.key === key)) continue;
    group.fields.push({
      key,
      label,
      inputType,
      workbookStorageKeys: storageKeys,
    });
  }

  return { ...base, subGroups: groups };
}

export const ALL_PGA_CODES = Object.keys(PGA_FLAG_SCHEMAS) as PgaCode[];

const pgaRowValue = (
  row: Record<string, string | number>,
  keys: string[],
): string => {
  for (const key of keys) {
    const exact = row[key];
    if (exact !== undefined && exact !== null && String(exact).trim()) {
      return String(exact).trim();
    }
  }
  const normalizedKeys = new Set(
    keys.map((key) => key.toLowerCase().replace(/[^a-z0-9]/g, '')),
  );
  const match = Object.entries(row).find(([key, value]) => {
    const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalizedKeys.has(normalized) && String(value ?? '').trim();
  });
  return match ? String(match[1]).trim() : '';
};

// CBP's PGA Name Code (e.g. EPA) and PGA Program Code (e.g. VNE) name the
// program directly; the agency/list text checks below are the fallback for
// rows without them. ODS (ozone-depleting substances) shares EPA HFC's Clean
// Air Act Title VI program, and NOAA programs without their own code (e.g.
// HMS) fall back to SIMP as before. Keep in step with the product service's
// resolvePgaProgramCode.
const PGA_CODE_BY_CBP_AGENCY: Record<string, PgaCode> = {
  FDA: 'FDA',
  NHT: 'NHT',
  CPS: 'CPS',
  DEA: 'DEA',
  TTB: 'TTB',
  FWS: 'FWS',
  AMS: 'AMS',
  FSI: 'FSIS',
  OMC: 'OMC',
  ATF: 'ATF',
  DDT: 'DTC',
  DTC: 'DTC',
};

const APHIS_CORE_PROGRAM_CODES = new Set(['AAC', 'ABS', 'APQ', 'AVS']);

// Agencies with several programs resolve from the program code, then the
// program/list text (a row may carry the agency code without a program code),
// and only then their default.
const resolveFromCbpCodes = (
  agencyCode: string,
  programCode: string,
  programText: string,
): PgaCode | null => {
  if (agencyCode === 'APH') {
    if (programCode === 'APL') return 'APH_LACEY';
    if (APHIS_CORE_PROGRAM_CODES.has(programCode)) return 'APH_CORE';
    return programText.includes('LACEY') ? 'APH_LACEY' : 'APH_CORE';
  }
  if (agencyCode === 'EPA') {
    if (programCode === 'VNE') return 'EPA_VNE';
    if (programCode === 'HFC' || programCode === 'ODS') return 'EPA_HFC';
    if (programCode.startsWith('PS')) return 'EPA_PST';
    if (programCode === 'TS1') return 'EPA_TSCA';
    if (programText.includes('HFC')) return 'EPA_HFC';
    if (programText.includes('PEST') || programText.includes('FIFRA'))
      return 'EPA_PST';
    if (programText.includes('VEHIC') || programText.includes('ENGINE'))
      return 'EPA_VNE';
    return 'EPA_TSCA';
  }
  if (agencyCode === 'NMF') {
    if (programCode === 'SIM') return 'NMF_SIM';
    if (programCode === '370') return 'NMF_370';
    if (programCode === 'AMR') return 'NMF_AMR';
    if (programCode === 'COA') return 'NMF_COA';
    if (programText.includes('SIMP')) return 'NMF_SIM';
    if (programText.includes('370') || programText.includes('TUNA'))
      return 'NMF_370';
    if (programText.includes('AMR') || programText.includes('ANTARCTIC'))
      return 'NMF_AMR';
    if (programText.includes('COA')) return 'NMF_COA';
    return 'NMF_SIM';
  }
  return PGA_CODE_BY_CBP_AGENCY[agencyCode] ?? null;
};

// Map an OpenSearch-style row's agency/program to a PGA code where possible.
export function resolvePgaCode(
  row: Record<string, string | number>,
): PgaCode | null {
  const direct = pgaRowValue(row, [
    'PGA Flag Code',
    'PGA Code',
    'PGA_CODE',
    'pga_flag_code',
    'pga_code',
  ])
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  if (direct && (ALL_PGA_CODES as string[]).includes(direct))
    return direct as PgaCode;
  const program = pgaRowValue(row, [
    'Program',
    'List',
    'List Name',
  ]).toUpperCase();
  const fromCbpCodes = resolveFromCbpCodes(
    pgaRowValue(row, ['PGA Name Code', 'pga_name_code']).toUpperCase(),
    pgaRowValue(row, ['PGA Program Code', 'pga_program_code']).toUpperCase(),
    program,
  );
  if (fromCbpCodes) return fromCbpCodes;
  const agency = pgaRowValue(row, ['Agency']).toUpperCase();
  const source = `${agency} ${program}`;
  const agencyHas = (token: string): boolean =>
    agency.split(/[^A-Z0-9]+/).includes(token);
  if (agencyHas('FDA') || source.includes('FOOD AND DRUG')) return 'FDA';
  if (agencyHas('NHTSA') || source.includes('HIGHWAY TRAFFIC SAFETY'))
    return 'NHT';
  if (agencyHas('CPSC') || source.includes('CONSUMER PRODUCT SAFETY'))
    return 'CPS';
  if (agencyHas('DEA') || source.includes('DRUG ENFORCEMENT')) return 'DEA';
  if (agencyHas('ATF')) return 'ATF';
  if (agencyHas('TTB') || source.includes('ALCOHOL AND TOBACCO TAX'))
    return 'TTB';
  if (
    agencyHas('FSIS') ||
    source.includes('FOOD SAFETY INSPECTION') ||
    source.includes('FOOD SAFETY AND INSPECTION')
  )
    return 'FSIS';
  if (
    agencyHas('FWS') ||
    agency.includes('USFWS') ||
    source.includes('FISH AND WILDLIFE')
  )
    return 'FWS';
  if (
    agencyHas('NMFS') ||
    agencyHas('NOAA') ||
    source.includes('MARINE FISHERIES')
  ) {
    if (program.includes('SIMP')) return 'NMF_SIM';
    if (program.includes('370') || program.includes('TUNA')) return 'NMF_370';
    if (program.includes('AMR') || program.includes('ANTARCTIC'))
      return 'NMF_AMR';
    if (program.includes('COA')) return 'NMF_COA';
    return 'NMF_SIM';
  }
  if (agencyHas('APHIS') || source.includes('ANIMAL AND PLANT HEALTH'))
    return program.includes('LACEY') ? 'APH_LACEY' : 'APH_CORE';
  if (
    agency === 'AMS' ||
    source.includes('AGRICULTURAL MARKETING SERVICE') ||
    (agencyHas('USDA') && !agencyHas('APHIS'))
  )
    return 'AMS';
  if (
    agencyHas('EPA') ||
    agencyHas('TSCA') ||
    source.includes('ENVIRONMENTAL PROTECTION')
  ) {
    if (program.includes('HFC')) return 'EPA_HFC';
    if (program.includes('PEST') || program.includes('FIFRA')) return 'EPA_PST';
    if (program.includes('VEHIC') || program.includes('ENGINE'))
      return 'EPA_VNE';
    return 'EPA_TSCA';
  }
  if (
    agencyHas('DDTC') ||
    agency === 'DOT' ||
    source.includes('DEFENSE TRADE CONTROLS')
  )
    return 'DTC';
  if (
    agencyHas('OMC') ||
    program.includes('USML') ||
    source.includes('OFFICE OF MARINE CONSERVATION')
  )
    return 'OMC';
  return null;
}

// Compute total fields (including default 1 instance for each repeater) + how
// many are filled in a given value map.
export function computeCompleteness(
  schema: PgaFlagSchema,
  values: Record<string, unknown>,
  repeaters: Record<string, Record<string, unknown>[]>,
): { filled: number; total: number; requiredMissing: number } {
  const normalized = normalizePgaFlagPayload(
    schema.code,
    values,
    repeaters,
    schema,
  );
  const normalizedValues = normalized.values;
  const normalizedRepeaters = normalized.repeaters;
  let filled = 0;
  let total = 0;
  let requiredMissing = 0;
  for (const sg of schema.subGroups) {
    if (sg.repeatable) {
      const instances = normalizedRepeaters[sg.key] ?? [];
      // count 1 instance minimum for schema total, but only if there's data or required
      const effective = Math.max(instances.length, 0);
      for (const inst of instances) {
        for (const f of sg.fields) {
          total++;
          const v = inst[f.key];
          if (v !== undefined && v !== '' && v !== null) filled++;
          else if (f.required) requiredMissing++;
        }
      }
      // If no instances yet, count required fields as missing (1 phantom instance)
      if (effective === 0) {
        for (const f of sg.fields) {
          total++;
          if (f.required) requiredMissing++;
        }
      }
    } else {
      for (const f of sg.fields) {
        total++;
        const v = normalizedValues[f.storageKey ?? `${sg.key}.${f.key}`];
        if (v !== undefined && v !== '' && v !== null) filled++;
        else if (f.required) requiredMissing++;
      }
    }
  }
  return { filled, total, requiredMissing };
}

export function getRequiredFieldKeys(schema: PgaFlagSchema): string[] {
  const keys: string[] = [];
  for (const sg of schema.subGroups) {
    if (sg.repeatable) continue;
    for (const f of sg.fields)
      if (f.required) keys.push(f.storageKey ?? `${sg.key}.${f.key}`);
  }
  return keys;
}

// Colour hint per agency family — reuses existing badge tokens.
export function pgaBadgeTone(code: PgaCode): 'info' | 'warning' | 'danger' {
  if (code === 'OMC' || code === 'ATF' || code === 'DEA') return 'danger';
  if (code.startsWith('EPA') || code === 'APH_LACEY' || code === 'FWS')
    return 'warning';
  return 'info';
}
